// @flow
import {
    AspectRatioCard,
    AspectRatioCardImage,
    AspectRatioCardTitle
} from '@homeaway/react-aspect-ratio-card';
import CardCarousel from '@homeaway/react-card-carousel';
import Experiment from '@homeaway/react-experiment';
import {debounce} from 'lodash';
import React, {Component} from 'react';
import {injectIntl} from 'react-intl';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {HA_SERP_SEARCH_TERM_CAROUSEL2} from '../../abacus/experiments';
import routerActions from '../../actions/router';
import keyboardConstants from '../../constants/keyboardConstants';
import {media} from '../../utils/breakpoints';
import {GENERIC_EVENT, trigger as edapTrigger} from '../../utils/edapWrapper';
import withLingua from '../withLingua/withLingua';
import './SearchTermCarousel.less';

import type {Node} from 'react';
import type {State as ReduxState} from '../../reducers/index';
import gql from 'graphql-tag';

import type {LinguaProps} from '../withLingua/withLingua';
import type {IntlShape} from 'react-intl';
import type DiscoveryFeeds from '@homeaway/graphql-traveler-discovery';

const LINGUA_KEYS = {
    carousel_title: 'carousel.popularPlaceTitle',
    carousel_next: 'carousel.next',
    carousel_previous: 'carousel.previous'
};

export const fragments = {
    results: gql`
        fragment SearchTermCarouselResults on Results {
            discoveryXploreFeeds {
                scrollInfo {
                    nextCursor
                }
                results {
                    id
                    title
                    items {
                        ... on SearchDiscoveryFeedItem {
                            type
                            imageHref
                            place {
                                uuid
                                name {
                                    full
                                    simple
                                }
                            }
                        }
                    }
                }
            }
        }
    `
};

type State = {
    index: number
};

type StateProps = {
    isXsScreenWidth: boolean,
    isBoundingBoxSearch?: boolean
};

type OwnProps = {
    aspectRatio: string,
    results: any,
    locationSearch: typeof routerActions.search,
    intl: IntlShape
};

type Props = OwnProps & StateProps & LinguaProps;

export class SearchTermCarousel extends Component<Props, State> {
    static defaultProps = {
        aspectRatio: '4x3'
    };

    constructor(props: Props) {
        super(props);
        this.state = {
            index: 0
        };
    }

    // handle onScroll event for mobile scrolling
    handleScroll = debounce(() => {
        edapTrigger(GENERIC_EVENT, {
            eventaction: 'searchTerm carousel',
            highfrequency: 'true',
            eventlabel: 'advance'
        });
    }, 200);

    // Centers the controls within the image using the method from AspectRatioCard
    getControlOffset = (aspectRatio: string) => {
        return (width: number, controlHeight: string) => {
            return AspectRatioCard.getControlOffset(
                width,
                controlHeight,
                aspectRatio
            );
        };
    };

    handlePrevNext = (
        evt: SyntheticEvent<HTMLInputElement>,
        newIndex: number
    ) => {
        this.setState({index: newIndex});
        edapTrigger(GENERIC_EVENT, {
            eventaction: 'searchTerm carousel',
            highfrequency: 'true',
            eventlabel: 'advance'
        });
    };

    handleOnClick = (evt: SyntheticEvent<HTMLInputElement>) => {
        let q = evt.currentTarget.getAttribute('value');
        if (q) {
            this.props.locationSearch({q});
        }

        const options = {delayProcessing: true};
        edapTrigger(
            GENERIC_EVENT,
            {
                eventaction: 'searchTerm carousel',
                eventlabel: 'click'
            },
            options
        );
    };

    renderCards = (discoveryXploreFeeds: DiscoveryFeeds) => {
        return (
            // purposely selecting first element in array
            discoveryXploreFeeds.results[0].items.map<Node>(
                (destination, i) => (
                    <div
                        className="SearchTermCarousel__link"
                        onClick={this.handleOnClick}
                        onKeyDown={this.handleIfEnterPressed}
                        value={destination.place.name.full}
                        role="button"
                        tabIndex="0"
                        key={`card-${i + 1}`}
                    >
                        <AspectRatioCard aspectRatio={this.props.aspectRatio}>
                            <AspectRatioCardImage
                                data-slot={'figure'}
                                imageUri={destination.imageHref}
                            />
                            <AspectRatioCardTitle
                                data-slot={'caption'}
                                title={destination.place.name.simple}
                            />
                        </AspectRatioCard>
                    </div>
                )
            )
        );
    };

    handleIfEnterPressed = (evt: SyntheticEvent<HTMLInputElement>) => {
        if (
            evt.nativeEvent instanceof KeyboardEvent &&
            evt.nativeEvent.keyCode === keyboardConstants.ENTER
        ) {
            this.handleOnClick(evt);
        }
    };

    render() {
        const {props} = this;
        const {formatMessage} = props.intl;
        const nextLabel = props.messages[LINGUA_KEYS.carousel_next];
        const previousLabel = props.messages[LINGUA_KEYS.carousel_previous];
        const title = props.messages[LINGUA_KEYS.carousel_title];
        const {searchTerm, discoveryXploreFeeds} = this.props.results;

        if (this.props.isBoundingBoxSearch) {
            return <div />;
        }

        const popularPlaceTitle = formatMessage(
            {
                id: 'popularPlaceTitle',
                defaultMessage: title
            },
            {
                placeName: searchTerm.name
            }
        );

        return (
            <Experiment
                name={HA_SERP_SEARCH_TERM_CAROUSEL2}
                variant={Experiment.ON}
            >
                <div onScroll={this.handleScroll}>
                    <CardCarousel
                        breakpoints={{
                            '1200': 7,
                            '992': 7,
                            '768': 5,
                            '480': 3,
                            '0': 3
                        }}
                        cardIndex={this.state.index}
                        getControlOffset={this.getControlOffset(
                            props.aspectRatio
                        )}
                        onNextClick={this.handlePrevNext}
                        onPreviousClick={this.handlePrevNext}
                        nextLabel={nextLabel}
                        previousLabel={previousLabel}
                        title={popularPlaceTitle}
                        scrolling={props.isXsScreenWidth}
                    >
                        {this.renderCards(discoveryXploreFeeds)}
                    </CardCarousel>
                </div>
            </Experiment>
        );
    }
}

const mapStateToProps = (state: ReduxState): StateProps => {
    let {viewport, searchParams} = state;
    const breakpoint = viewport.breakpoint;
    const isXsScreenWidth = !media(breakpoint, {
        minWidth: 'SM_MIN'
    });
    const isBoundingBoxSearch = searchParams.isBoundingBoxSearch;

    return {
        isXsScreenWidth,
        isBoundingBoxSearch
    };
};

const mapDispatchToProps = {
    locationSearch: routerActions.locationSearch
};

export default compose(
    connect(
        mapStateToProps,
        mapDispatchToProps
    ),
    injectIntl,
    withLingua({
        namespace: 'discovery/xplorer',
        category: 'messages'
    })
)(SearchTermCarousel);
